import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * Design system §6: ink fill, radius 8, 12.5px label. Icon-only controls must always
 * carry one, with their shortcut — see `TooltipShortcut`. Motion is 180ms fade + 4px rise.
 */
function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'z-50 flex w-fit origin-(--radix-tooltip-content-transform-origin) items-center gap-2 rounded-md bg-ink px-2.5 py-1.5 text-[12.5px] leading-[1.3] text-balance text-surface-1',
          'animate-in duration-(--animate-duration-popover) ease-(--ease-standard) fade-in-0 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/** The mono-11 shortcut hint that sits to the right of a tooltip label. */
function TooltipShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span data-slot="tooltip-shortcut" className={cn('font-mono text-[11px] text-eyebrow', className)} {...props} />
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipShortcut };
